import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { usePageTitle } from '../lib/usePageTitle'

/**
 * Maratontabellen utan inloggning.
 *
 * Hämtar via `marathon_public`, som bara returnerar smeknamn och siffror.
 * Sidan känner varken user_id, riktigt namn eller e-post — inte för att den
 * låter bli att visa dem, utan för att den aldrig får dem.
 *
 * Säsongen kommer ur URL:en i stället för ur en lista, eftersom `seasons`
 * medvetet inte är läsbar för utloggade. Det håller den publika ytan vid
 * exakt två funktioner.
 */
interface PublicStanding {
  nickname: string
  majors_played: number
  majors_total: number
  score: number
  best_finish: number | null
  wins: number
  mode: string
}

/**
 * v2:s forsta sasong. Historiken 2021-2026 raknades i v1 och ligger i
 * archive_results, som bar riktiga namn och darfor aldrig kan bli publik.
 * Utan den har gransen defaultar sidan till innevarande ar och visar en tom
 * tabell for ett ar som i verkligheten ar fardigspelat — vilket later som
 * att ingen spelat, inte som att siffrorna bor nagon annanstans.
 */
const FIRST_SEASON = 2027

export function PublicBoard() {
  const { season: seasonParam } = useParams()
  const { session } = useAuth()
  const currentYear = new Date().getFullYear()
  const latest = Math.max(currentYear, FIRST_SEASON)
  const season = Number(seasonParam) || latest
  usePageTitle(`Tabellen ${season}`)

  const [rows, setRows] = useState<PublicStanding[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error } = await supabase.rpc('marathon_public', { p_season: season })
    if (error) setError(error.message)
    else setRows((data as PublicStanding[]) ?? [])
    setLoading(false)
  }, [season])

  useEffect(() => {
    void load()
  }, [load])

  // Sidtiteln följer säsongen. index.html:s statiska titel gäller annars för
  // varje vy, vilket ger identiska flikar och identiska bokmärken.
  useEffect(() => {
    document.title = `Maratontabellen ${season} · Majortipset`
    return () => {
      document.title = 'Majortipset'
    }
  }, [season])

  const mode = rows[0]?.mode ?? 'points'
  const scoreLabel = mode === 'points' ? 'Poäng' : 'Placeringssumma'

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Majortipset {season}</p>
        <h1>Maratontabellen</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      <div className="card stack">
        <p className="muted">
          {mode === 'points'
            ? 'Poäng från årets fyra majors. Högst poäng vinner, en missad major ger noll.'
            : 'Summan av placeringarna i årets fyra majors. Lägst summa vinner.'}
        </p>
        {/* Navigeringen begransas till sasonger v2 kan ha rader for. Utan
            granserna gick det att vandra bakat i det oandliga genom ar som
            alltid ar tomma. */}
        {(season > FIRST_SEASON || season < latest) && (
          <div style={{ display: 'flex', gap: 12 }}>
            {season > FIRST_SEASON && (
              <Link className="link" to={`/tabell/${season - 1}`}>
                ← {season - 1}
              </Link>
            )}
            {season < latest && (
              <Link className="link" to={`/tabell/${season + 1}`}>
                {season + 1} →
              </Link>
            )}
          </div>
        )}
      </div>

      {loading && <p className="muted">Hämtar…</p>}

      {!loading && rows.length === 0 && (
        <div className="card stack">
          <p className="muted">
            Ingen färdigspelad major {season} än. Tabellen fylls i takt med att
            majorerna avslutas.
          </p>
          {season === FIRST_SEASON && (
            <p className="muted">
              Majortipset räknas i det här systemet från och med Masters {FIRST_SEASON}.
              Säsongerna dessförinnan finns kvar — de visas för den som är inloggad,
              eftersom de står med deltagarnas riktiga namn.
            </p>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="board">
          <div className="marathon__head">
            <span className="board__pos">#</span>
            <span className="board__name">Smeknamn</span>
            <span className="marathon__cell">Majors</span>
            <span className="marathon__cell">Bäst</span>
            <span className="marathon__cell">Segrar</span>
            <span className="marathon__score">{scoreLabel}</span>
          </div>

          {rows.map((r, i) => (
            <div key={`${r.nickname}-${i}`} className="marathon__row">
              <span className="board__pos">{i + 1}</span>
              <span className="board__name">{r.nickname}</span>
              <span className="marathon__cell">
                {r.majors_played}/{r.majors_total}
              </span>
              <span className="marathon__cell">{r.best_finish ?? '–'}</span>
              <span className="marathon__cell">{r.wins || '–'}</span>
              <span className="marathon__score">{r.score}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <p className="muted">
          {session ? (
            <>
              Du är inloggad. <Link to="/maraton">Maratontabellen med riktiga namn</Link>,
              gäng och lag ligger kvar bakom inloggningen.
            </>
          ) : (
            <>
              Här visas bara smeknamn. <Link to="/logga-in">Logga in</Link> för att se
              vem som är vem, ditt gäng och dina lag.
            </>
          )}
        </p>
      </div>
    </div>
  )
}
